import { useInfiniteQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '@/config/constants'
import { requireAuthToken, type ListQueryParams } from '@/lib/api'
import { clinicalDataKeys } from '@/lib/queryKeys'
import {
  fetchClinicalDataPage,
  type ClinicalDataEndpoint,
} from '@/features/clinical-data/api/api'

export function useClinicalDataQuery<T>({
  endpoint,
  params,
  mapRow,
  enabled = true,
}: {
  endpoint: ClinicalDataEndpoint
  params: ListQueryParams
  mapRow: (row: Record<string, unknown>) => T
  enabled?: boolean
}) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery({
    // The organization ID isolates each tenant's cache. Intentionally omit
    // placeholderData so an organization change cannot retain prior-org rows.
    queryKey: clinicalDataKeys.list(organization?.id, endpoint, params),
    enabled: !!organization?.id && enabled,
    queryFn: async ({ pageParam, signal }) => {
      const token = await requireAuthToken(getToken)
      return fetchClinicalDataPage({
        token,
        endpoint,
        params,
        mapRow,
        cursor: pageParam as string | undefined,
        signal,
      })
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
